import {
  Row,
  Col,
  Card,
  Switch,
  Input,
  Table,
  Typography,
  Button,
  Modal,
  Space,
  Select,
  Tooltip,
  Popover,
  message,
  Drawer,
  Spin,
  Descriptions,
  Tag,
  Form,
  Divider
} from "antd";
import { useState, useEffect, useRef } from "react";
import { 
  SearchOutlined, 
  SyncOutlined, 
  EditOutlined
} from "@ant-design/icons";
import { api } from '../services/authService';
import { formatDate } from '../utils/format';
import useGetCurrencies from '../hooks/useGetCurrencies';
import useGetCurrency from '../hooks/useGetCurrency';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isSuperAdmin, isAdmin } from '../utils/permissions';

function CurrencyManagement() {
  const queryClient = useQueryClient();
  
  // Fetch user profile to get user ID
  const { data: userProfile } = useQuery({
    queryKey: ['admin-profile'],
    queryFn: async () => {
      const response = await api.get('/admin/user-profile');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch user roles
  const { data: userRoles = [] } = useQuery({
    queryKey: ['user-roles', userProfile?.id],
    queryFn: async () => {
      if (!userProfile?.id) return [];
      const response = await api.get(`/admin/user-roles?user_id=${userProfile.id}`);
      return response.data.user_roles || [];
    },
    enabled: !!userProfile?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Check if user has admin or super admin privileges using centralized functions
  const hasSuperAdminAccess = isSuperAdmin(userProfile, userRoles);
  const hasAdminAccess = isAdmin(userProfile, userRoles);
  
  console.log("=== Currency User Roles Debug ===");
  console.log("User roles:", userRoles);
  console.log("User profile:", userProfile);
  console.log("User profile role:", userProfile?.role);
  console.log("User profile is_super:", userProfile?.is_super);
  console.log("Has super admin access:", hasSuperAdminAccess);
  console.log("Has admin access:", hasAdminAccess);
  console.log("=== End Currency User Roles Debug ===");

  const [searchText, setSearchText] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Use React Query hooks
  const {
    data: currencies,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError: isListError,
    error: listError,
    refetch: refetchCurrencies,
  } = useGetCurrencies({ search: searchText });

  const {
    data: currencyDetails,
    isLoading: detailsLoading,
    isError: isDetailsError,
    error: detailsError,
  } = useGetCurrency(selectedCurrencyId);

  // Debug currency details
  console.log("=== Currency Details Debug ===");
  console.log("Selected currency ID:", selectedCurrencyId);
  console.log("Currency details:", currencyDetails);
  console.log("Details loading:", detailsLoading);
  console.log("Details error:", isDetailsError);
  console.log("Details error message:", detailsError);
  console.log("Details error response:", detailsError?.response?.data);
  console.log("Details error status:", detailsError?.response?.status);
  console.log("=== End Currency Details Debug ===");

  // Search handler
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      let trimmed = e.target.value.trim();
      if (!trimmed) {
        setSearchText("");
        return;
      }
      // Remove spaces from code param
      trimmed = trimmed.replace(/\s+/g, '');
      setSearchText(trimmed.toUpperCase());
    }
  };

  const handleSearchChange = (e) => {
    setInputValue(e.target.value);
  };

  // Refresh handler - always clears and refreshes, never searches
  const handleRefresh = () => {
    setSearchText("");
    setInputValue("");
    refetchCurrencies();
  };

  // Open modal and fetch details
  const handleViewDetails = (id) => {
    console.log("=== Currency View Details Debug ===");
    console.log("Opening modal for currency ID:", id);
    setSelectedCurrencyId(id);
    setModalVisible(true);
    console.log("Modal visible set to true");
    console.log("=== End Currency View Details Debug ===");
  };



  // Close modal
  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedCurrencyId(null);
  };

  // Clear search
  const handleClear = () => {
    setSearchText("");
    setInputValue("");
  };

  // Infinite scroll handler
  const handleTableScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };



  // Show error message if list fails to load
  if (isListError) {
    message.error(listError?.message || 'Failed to fetch currencies');
  }

  // Table columns as per new spec
  const columns = [
    {
      title: 'Currency',
      dataIndex: 'code',
      key: 'code',
      render: (code) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img
            src={`https://wise.com/public-resources/assets/flags/rectangle/${code.toLowerCase()}.png`}
            alt={code}
            style={{ width: 24, height: 16, borderRadius: 2, flexShrink: 0 }}
          />
          <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>{code}</Typography.Text>
        </div>
      ),
    },
    {
      title: 'Full Name',
      dataIndex: 'fullname',
      key: 'fullname',
      render: (fullname) => fullname || '-',
    },
    {
      title: 'Created Date',
      dataIndex: 'created',
      key: 'created',
      render: (created) => formatDate(created),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => handleViewDetails(record.id)}>
          View Details
        </Button>
      ),
    },
  ];

  const [editingField, setEditingField] = useState(null); // 'fullname', 'name', 'disburse_types'
  const [editValue, setEditValue] = useState("");
  const [editDisburseTypes, setEditDisburseTypes] = useState([]);
  const [savingField, setSavingField] = useState(false);

  const handleEditField = (field) => {
    setEditingField(field);
    if (field === "disburse_types") {
      setEditDisburseTypes(currencyDetails.supported_disburse_types ? [...currencyDetails.supported_disburse_types] : [{ name: '', nice_name: '' }]);
    } else {
      setEditValue(currencyDetails[field] || "");
    }
  };
  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue("");
    setEditDisburseTypes([]);
  };
  const handleSaveField = async () => {
    setSavingField(true);
    try {
      // Only supported_disburse_types can be updated via PATCH
      const updatedDisburseTypes = editDisburseTypes.filter(dt => dt.name);
      const response = await api.patch(`/admin/currencies/${currencyDetails.id}`, {
        supported_disburse_types: updatedDisburseTypes
      });
      
      message.success('Currency updated');
      
      // Update the query cache immediately for real-time updates
      queryClient.setQueryData(['currency', selectedCurrencyId], (oldData) => ({
        ...oldData,
        supported_disburse_types: updatedDisburseTypes
      }));
      
      // Also invalidate to ensure fresh data from server
      queryClient.invalidateQueries({
        queryKey: ['currency', selectedCurrencyId],
        exact: true
      });
      queryClient.invalidateQueries({
        queryKey: ['currencies'],
        exact: true
      });
      
      // Refresh the currencies list after successful edit
      refetchCurrencies();
      
      setEditingField(null);
      setEditValue("");
      setEditDisburseTypes([]);
    } catch (err) {
      message.error(err.message || 'Failed to update currency');
    } finally {
      setSavingField(false);
    }
  };
  const handleEditDisburseTypeChange = (idx, field, value) => {
    const updated = [...editDisburseTypes];
    updated[idx][field] = value;
    setEditDisburseTypes(updated);
  };
  const handleAddEditDisburseType = () => {
    setEditDisburseTypes([...editDisburseTypes, { name: '', nice_name: '' }]);
  };
  const handleRemoveEditDisburseType = (idx) => {
    setEditDisburseTypes(editDisburseTypes.filter((_, i) => i !== idx));
  };

  return (
    <div className="layout-content">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card bordered={false}>
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  Currency Management
                </Typography.Title>
              </Col>
              <Col>
                <Space size={16} style={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Input
                    placeholder="Search by Currency code"
                    prefix={<SearchOutlined />}
                    style={{ width: 200 }}
                    value={inputValue}
                    onChange={handleSearchChange}
                    onPressEnter={handleSearchKeyDown}
                    allowClear
                    onClear={handleClear}
                  />
                  <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    onClick={handleRefresh}
                    loading={isFetching && !isFetchingNextPage}
                  >
                    Refresh
                  </Button>
                </Space>
              </Col>
            </Row>
            <div style={{ marginTop: 24, maxHeight: 600, overflow: 'auto', position: 'relative' }} onScroll={handleTableScroll}>
              <Table
                dataSource={currencies}
                columns={columns}
                loading={isFetching && !isFetchingNextPage}
                rowKey="id"
                pagination={false}
              />
              {isFetchingNextPage && (
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <Spin />
                </div>
              )}
            </div>
            <Modal
              title="Currency Details"
              open={modalVisible}
              onCancel={handleCloseModal}
              footer={null}
              width={700}
            >
              {console.log("=== Modal Render Debug ===")}
              {console.log("Modal visible:", modalVisible)}
              {console.log("Has super admin access:", hasSuperAdminAccess)}
              {console.log("Selected currency ID:", selectedCurrencyId)}
              {console.log("Details loading:", detailsLoading)}
              {console.log("Details error:", isDetailsError)}
              {console.log("Currency details:", currencyDetails)}
              {console.log("Currency details type:", typeof currencyDetails)}
              {console.log("Currency details keys:", currencyDetails ? Object.keys(currencyDetails) : 'null')}
              {console.log("=== End Modal Render Debug ===")}
              
              {detailsLoading ? (
                <Spin />
              ) : isDetailsError ? (
                <Typography.Text type="danger">{detailsError?.message || 'Failed to fetch currency details'}</Typography.Text>
              ) : currencyDetails ? (
                <>
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Currency Code">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img
                          src={`https://wise.com/public-resources/assets/flags/rectangle/${currencyDetails.code.toLowerCase()}.png`}
                          alt={currencyDetails.code}
                          style={{ width: 24, height: 16, borderRadius: 2, flexShrink: 0 }}
                        />
                        <Typography.Text strong>{currencyDetails.code}</Typography.Text>
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="Full Name">
                      <Space>
                        {currencyDetails.fullname || '-'}
                      </Space>
                    </Descriptions.Item>
                    {/* Only show Short Name if it's different from Full Name */}
                    {currencyDetails.name && currencyDetails.name !== currencyDetails.fullname && (
                      <Descriptions.Item label="Short Name">
                        <Space>
                          {currencyDetails.name || '-'}
                        </Space>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Created Date">{formatDate(currencyDetails.created)}</Descriptions.Item>
                    <Descriptions.Item label="Supported Disburse Types">
                      {editingField === 'disburse_types' ? (
                        <div style={{ maxHeight: 350, overflowY: 'auto', background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: 16, marginBottom: 16 }}>
                          {editDisburseTypes.map((dt, idx) => (
                            <div key={idx} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: idx !== editDisburseTypes.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                              <Row gutter={8} align="middle">
                                <Col span={10}>
                                  <Input
                                    value={dt.name}
                                    onChange={e => handleEditDisburseTypeChange(idx, 'name', e.target.value)}
                                    placeholder="Disburse Type"
                                    style={{ width: '100%' }}
                                  />
                                </Col>
                                <Col span={12}>
                                  <Input
                                    value={dt.nice_name}
                                    onChange={e => handleEditDisburseTypeChange(idx, 'nice_name', e.target.value)}
                                    placeholder="Display Name"
                                    style={{ width: '100%' }}
                                  />
                                </Col>
                                <Col span={2} style={{ textAlign: 'right' }}>
                                  {editDisburseTypes.length > 1 && (
                                    <Button danger onClick={() => handleRemoveEditDisburseType(idx)} size="small">Remove</Button>
                                  )}
                                </Col>
                              </Row>
                            </div>
                          ))}
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                            <Button type="dashed" onClick={handleAddEditDisburseType} size="small">
                              Add Disburse Type
                            </Button>
                          </div>
                          <Divider style={{ margin: '12px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, position: 'sticky', bottom: 0, background: '#fafafa', paddingTop: 8 }}>
                            <Button type="primary" loading={savingField} onClick={handleSaveField}>
                              Save
                            </Button>
                            <Button onClick={handleCancelEdit}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Space direction="vertical">
                          {currencyDetails.supported_disburse_types && currencyDetails.supported_disburse_types.length > 0 ? (
                            currencyDetails.supported_disburse_types.map((type, idx) => (
                              <Tag key={idx}>{type.nice_name || type.name}</Tag>
                            ))
                          ) : (
                            <Typography.Text type="secondary">None</Typography.Text>
                          )}
                          {editMode && hasSuperAdminAccess && (
                            <Tooltip title="Edit Supported Disburse Types">
                              <Button icon={<EditOutlined />} size="small" onClick={() => handleEditField('disburse_types')} />
                            </Tooltip>
                          )}
                        </Space>
                      )}
                    </Descriptions.Item>
                    {currencyDetails.source_currencies && currencyDetails.source_currencies.length > 0 && (
                      <Descriptions.Item label="Source Currencies">
                        <Space wrap>
                          {currencyDetails.source_currencies.map((cur, idx) => (
                            <Tag key={idx}>{cur}</Tag>
                          ))}
                        </Space>
                      </Descriptions.Item>
                    )}
                    {currencyDetails.dest_currencies && currencyDetails.dest_currencies.length > 0 && (
                      <Descriptions.Item label="Destination Currencies">
                        {currencyDetails.dest_currencies.join(', ')}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                    {!editMode ? (
                      hasSuperAdminAccess ? (
                        <Button 
                          type="primary" 
                          onClick={() => setEditMode(true)}
                        >
                          Edit
                        </Button>
                      ) : null
                    ) : (
                      <Button onClick={() => { setEditMode(false); setEditingField(null); }}>
                        Done
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <Typography.Text type="secondary">
                    Loading currency details...
                  </Typography.Text>
                </div>
              )}
            </Modal>

          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default CurrencyManagement;